/**
 * iter733 — Extracted from SalesPage.jsx. Sticky-bottom summary bar
 * showing Grand Total + Cash/Bank/Online/Credit splits across the
 * currently filtered (post AdvancedSearch) sales list. All testids
 * preserved.
 */
import { RiyalIcon } from '@/lib/currency';
import { applySearchFilters } from '@/components/AdvancedSearch';
import { useVatModeStore } from '@/stores/vatModeStore';
import { VatModeToggle } from '@/components/common/VatModeToggle';

export function SalesGrandTotalBar({ sales, searchFilters, amountView, serverTotals }) {
  const inclVat = useVatModeStore((st) => st.inclVat);
  const allSales = applySearchFilters(sales.map(s => ({
    ...s, payment_mode: s.payment_details?.[0]?.mode || 'cash'
  })), searchFilters);
  const clientTotals = allSales.reduce((acc, s) => {
    const gross = s.amount || 0;
    const net = s.final_amount != null ? s.final_amount : (gross - (s.discount || 0));
    acc.total += amountView === 'gross' ? gross : net;
    acc.vat += s.tax_amount || 0;
    (s.payment_details || []).forEach(p => {
      const mode = p.mode || 'cash';
      const pAmt = p.amount || 0;
      if (mode === 'cash') acc.cash += pAmt;
      else if (mode === 'bank' || mode === 'card') acc.bank += pAmt;
      else if (mode === 'credit') acc.credit += pAmt;
      else if (mode === 'online' || mode === 'online_platform') acc.online += pAmt;
    });
    return acc;
  }, { total: 0, cash: 0, bank: 0, credit: 0, online: 0, vat: 0 });

  // Server aggregates cover the FULL filtered window (branch/date/payment
  // mode/amount range all already forwarded to the backend), not just the
  // currently-loaded page — prefer them so totals don't undercount once a
  // filtered window exceeds one page. Only NET mode is computed server-side
  // today (matches BranchSummaryCard's existing gross/net split), and a live
  // free-text search narrows client-side beyond what the backend regex
  // covers, so both fall back to the on-page reduce.
  const useServer = amountView === 'net' && serverTotals && !searchFilters?.search;
  const totals = useServer ? serverTotals : clientTotals;
  const salesCount = useServer ? (serverTotals.count ?? allSales.length) : allSales.length;

  const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-7 gap-2 mb-4 mt-3 sticky bottom-0 z-20 bg-background/95 backdrop-blur-sm py-2 -mx-1 px-1 rounded-lg border-t border-border/40 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.06)]"
      data-testid="sales-summary-bar-2"
    >
      <div className="p-3 rounded-xl bg-gradient-to-br from-stone-50 to-stone-100 border border-border card-enter">
        <div className="flex items-center justify-between gap-1"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Grand Total ({inclVat ? 'Incl. VAT' : 'Excl. VAT'})</div><VatModeToggle /></div>
        <div className="text-lg font-bold text-foreground inline-flex items-center gap-1" data-testid="sales-grand-total"><RiyalIcon size="0.7em" /> {fmt(inclVat ? totals.total : (totals.total || 0) - (totals.vat || 0))}</div>
        <div className="text-[10px] text-muted-foreground">{salesCount} sales · VAT {fmt(totals.vat || 0)} · payments below incl. VAT</div>
      </div>
      <div className="p-3 rounded-xl bg-success/10 border border-success/30 card-enter">
        <div className="text-[10px] text-success uppercase tracking-wider">Cash</div>
        <div className="text-lg font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.cash)}</div>
      </div>
      <div className="p-3 rounded-xl bg-info/10 border border-info/30 card-enter">
        <div className="text-[10px] text-info uppercase tracking-wider">Bank</div>
        <div className="text-lg font-bold text-info inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.bank)}</div>
      </div>
      <div className="p-3 rounded-xl bg-purple-50 border border-purple-200 card-enter">
        <div className="text-[10px] text-purple-600 uppercase tracking-wider">Online</div>
        <div className="text-lg font-bold text-purple-700 inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.online)}</div>
      </div>
      <div className="p-3 rounded-xl bg-warning/10 border border-warning/30 card-enter">
        <div className="text-[10px] text-warning uppercase tracking-wider">Credit</div>
        <div className="text-lg font-bold text-warning inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.credit)}</div>
      </div>
      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 card-enter" data-testid="sales-received-tile">
        <div className="text-[10px] text-emerald-600 uppercase tracking-wider">Received</div>
        <div className="text-lg font-bold text-emerald-700 inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.received ?? Math.max(0, (totals.total || 0) - (totals.receivable || 0)))}</div>
        <div className="text-[10px] text-muted-foreground" data-testid="sales-received-note">recorded · bank/POS not yet bank-verified</div>
      </div>
      <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 card-enter" data-testid="sales-receivable-tile">
        <div className="text-[10px] text-rose-600 uppercase tracking-wider">Receivable</div>
        <div className="text-lg font-bold text-rose-700 inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(totals.receivable || 0)}</div>
        {totals.platformPending != null && (
          <div className="text-[10px] text-muted-foreground" data-testid="sales-receivable-split">
            platform pending {fmt(totals.platformPending || 0)} · customer {fmt(Math.max(0, (totals.receivable || 0) - (totals.platformPending || 0)))}
          </div>
        )}
      </div>
    </div>
  );
}
